import { useState } from 'react'
import { INGREDIENT_BACK, getCardOrPlaceholder, ingredientImage } from '../data/ingredientCards'

interface Props {
  /** Ingredient id (e.g. 'butter'). Unknown ids render the deck back. */
  id: string
  /** When true, renders the deck back instead of the front. */
  faceDown?: boolean
  /** Visually mark the card as currently selected by the user. */
  selected?: boolean
  /** Size preset — heights match the Colorful card scale. */
  size?: 'sm' | 'md' | 'lg' | 'xl'
  /** Show the ingredient name in small text below the card. Default true. */
  showName?: boolean
  /** Optional click handler — wraps the card in a button when provided. */
  onSelect?: (id: string) => void
  disabled?: boolean
  className?: string
}

const SIZE_HEIGHT: Record<NonNullable<Props['size']>, string> = {
  sm: 'h-16',  // ~46×64
  md: 'h-24',  // ~70×96
  lg: 'h-32',  // ~93×128
  xl: 'h-44',  // ~128×176
}

const NAME_SIZE: Record<NonNullable<Props['size']>, string> = {
  sm: 'text-[10px]',
  md: 'text-xs',
  lg: 'text-sm',
  xl: 'text-sm',
}

const NAME_WIDTH: Record<NonNullable<Props['size']>, string> = {
  sm: 'max-w-14',
  md: 'max-w-20',
  lg: 'max-w-28',
  xl: 'max-w-36',
}

/**
 * Renders an I'd Eat That ingredient card using the real print art
 * from `public/games/iet/card-NNN.png`. The print files ship with bleed
 * — we crop the inner card by scaling the image up ~6% inside an
 * overflow-hidden frame so what the player sees is just the design,
 * no bleed margin.
 *
 * The ingredient name sits in small text under the card so it reads
 * cleanly even when the rotated corner-stamp on the artwork is too
 * small to make out at the chosen size.
 */
export function IngredientCardImage({
  id,
  faceDown = false,
  selected = false,
  size = 'md',
  showName = true,
  onSelect,
  disabled = false,
  className = '',
}: Props) {
  // Unknown ids (e.g. art-less weekly ingredients) resolve to a labelled
  // placeholder rather than disappearing — the deck back stands in for art.
  const card = getCardOrPlaceholder(id)
  const src = faceDown ? INGREDIENT_BACK : ingredientImage(id)
  const fallbackBg = card.color

  // If the image 404s (e.g. a print file hasn't been dropped for this
  // ingredient yet), hide the <img> entirely and let the coloured
  // background + name-below stand in. Avoids the ugly browser-default
  // broken-image icon.
  const [imgFailed, setImgFailed] = useState(false)

  const cardEl = (
    <div
      className={`relative aspect-597/822 ${SIZE_HEIGHT[size]} rounded-xl overflow-hidden
                  border-2 transition-all
                  ${selected ? 'border-brand-text shadow-card-lg' : 'border-transparent shadow-card'}`}
      style={{ backgroundColor: fallbackBg }}
    >
      {!imgFailed && (
        <img
          src={src}
          alt={card.name}
          loading="lazy"
          draggable={false}
          onError={() => setImgFailed(true)}
          className="absolute inset-0 w-full h-full object-cover select-none pointer-events-none"
          // Scale up slightly to crop the print bleed. 6% is enough for
          // the standard 1/8" bleed on the print files; tweak here if
          // future print runs use a different bleed.
          style={{ transform: 'scale(1.06)' }}
        />
      )}
      {selected && (
        <div className="absolute inset-0 flex items-center justify-center bg-black/20">
          <span className="text-white text-3xl drop-shadow-sm font-bold">✓</span>
        </div>
      )}
    </div>
  )

  const label = showName && !faceDown && (
    <p className={`${NAME_SIZE[size]} ${NAME_WIDTH[size]} font-medium text-brand-text
                   text-center truncate mt-1`}>
      {card.name}
    </p>
  )

  if (onSelect) {
    return (
      <button
        onClick={() => onSelect(id)}
        disabled={disabled}
        className={`flex flex-col items-center active:scale-95 transition-transform
                    disabled:opacity-50 ${className}`}
      >
        {cardEl}
        {label}
      </button>
    )
  }

  return (
    <div className={`flex flex-col items-center ${className}`}>
      {cardEl}
      {label}
    </div>
  )
}
